import React from 'react'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { classNames } from 'utils/styles'
import PropTypes from 'prop-types'
import filesize from 'filesize'
import styles from './UploadField.scss'
import { Button, Icon, Spinner } from './../../index'
import * as networkService from './../../../services/networkService'
import * as translationService from '../../../services/translationService'
import ErrorText from '../../ErrorText/ErrorText'
import { startDocumentUploading, endDocumentUploading } from '../../../actions/AppAction'
import { bytesToMB, checkIfFileExceedsSizeLimit } from '../utils'
import { MAX_FILE_SIZE } from '../../../utils/validation/constants'

const mapStateToProps = ({ FlowReducer }) => {
  const { application } = FlowReducer
  return {
    application,
  }
}

const allowedFileTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webm']

class UploadField extends React.Component {
  static propTypes = {
    limit: PropTypes.number,
    id: PropTypes.string,
    value: PropTypes.array,
    onChange: PropTypes.func,
    error: PropTypes.string,
    disabled: PropTypes.bool,
    application: PropTypes.any,
    startDocumentUploading: PropTypes.any,
    endDocumentUploading: PropTypes.any,
  }
  uploadFieldRef = React.createRef()

  state = {
    files: [],
    limit: 1,
    isUploading: false,
    errors: [],
  }

  componentDidMount() {
    this.setState({
      limit: this.props.limit || 1,
      files: this.props.value || [],
    })
  }

  handleChange = event => {
    const { limit } = this.props
    let hasReachedLimit = false
    let fileErrors = []
    const { files } = this.state
    let uploadFiles = event.target.files
    let uploadFilesLength = uploadFiles.length

    // Add al the files to the files state
    for (let i = 0; i < uploadFilesLength; i++) {
      const file = uploadFiles[i]
      let isValid = true

      if (!file.type || !allowedFileTypes.includes(file.type)) {
        isValid = false
        fileErrors.push(translationService.documentsFileTypeNotAllowed(file.name))
      }
      if (files.length + 1 > limit) {
        isValid = false
        !hasReachedLimit && fileErrors.push(translationService.documentsFileLimitExceeded(file.name, limit))
        hasReachedLimit = true
      }

      if (checkIfFileExceedsSizeLimit(file, MAX_FILE_SIZE)) {
        isValid = false
        fileErrors.push(translationService.documentsFileSizeExceeded(file.name, bytesToMB(file.size), MAX_FILE_SIZE))
      }

      if (isValid) {
        files.push({
          file: uploadFiles[i],
          isUploading: false,
        })
      }
    }

    this.setState({ files, errors: fileErrors }, async () => {
      await Promise.all(
        files.map(async (file, index) => {
          if (file.id) {
            return file
          }

          file.isUploading = true

          await this.handleUpload(file, index)

          return file
        }),
      )
      this.props.onChange(files)
      if (this.uploadFieldRef.current) {
        this.uploadFieldRef.current.value = ''
      }
    })

    // Upload all the new files
  }

  handleUpload = (file, index) => {
    const { files, errors } = this.state
    this.props.startDocumentUploading()
    networkService
      .uploadDocument(file.file, this.props.application.id)
      .then(response => {
        const { id, filename, filesize = file.file.size || 0 } = response.data
        files[index] = { id, originalFilename: filename, size: filesize }
        this.setState({ files })
        this.props.endDocumentUploading()
      })
      .catch(() => {
        errors.push(translationService.documentsFileUnableToUpload(file.file.name))
        this.setState({ errors })
        this.removeFile(index)
        this.props.endDocumentUploading()
      })
  }

  removeFile = index => {
    const { files, errors } = this.state
    const file = files[index]

    try {
      files.splice(index, 1)
      this.setState({ files }, async () => {
        await networkService.removeDocument(file.id, this.props.application.id)
        this.props.onChange(this.state.files)
      })
    } catch (err) {
      errors.push(translationService.documentsFileUnableToRemove())
      this.setState({ errors })
    }
  }

  render() {
    const { id, error, disabled } = this.props
    const { files, limit, errors } = this.state

    const mutliple = limit && limit > 1 ? true : false

    const uploadClasses = classNames(
      styles.Wrapper,
      files.length > 0 && styles.hasFiles,
      files.length < limit && styles.hasInput,
      disabled && styles.Disabled,
    )

    return (
      <div className={uploadClasses}>
        {files.length > 0 && (
          <div className={styles.Files}>
            {files.map((file, index) => {
              const handleRemove = () => {
                this.removeFile(index)
              }
              if (file.isUploading !== undefined) {
                const uploadFile = file.file
                return (
                  <div key={index} className={styles.File}>
                    <div className={styles.FileMeta}>
                      <strong>{uploadFile.name ? uploadFile.name : uploadFile.originalFilename}</strong>
                      <br />
                      {uploadFile.size && filesize(uploadFile.size)}
                    </div>
                    {file.error && <ErrorText>{file.error}</ErrorText>}
                    {file.isUploading ? (
                      <React.Fragment>
                        <Spinner />
                        <Button className={styles.Remove} plain onClick={handleRemove}>
                          <Icon source="bin" />
                        </Button>
                      </React.Fragment>
                    ) : (
                      <Button className={styles.Remove} plain onClick={handleRemove}>
                        <Icon source="bin" />
                      </Button>
                    )}
                  </div>
                )
              } else {
                return (
                  <div key={index} className={styles.File}>
                    <div className={styles.FileMeta}>
                      <strong>{file.originalFilename}</strong>
                      <br />
                      {file.size > 0 ? filesize(file.size) : ''}
                    </div>
                    <Button className={styles.Remove} plain onClick={handleRemove}>
                      <Icon source="bin" />
                    </Button>
                  </div>
                )
              }
            })}
          </div>
        )}
        {files.length < limit && (
          <React.Fragment>
            <input
              className={styles.Input}
              type="file"
              name={name}
              id={id}
              multiple={mutliple}
              ref={this.uploadFieldRef}
              onChange={this.handleChange}
              accept={['application/pdf', 'image/*']}
            />
            <label htmlFor={id} className={styles.Upload}>
              <span>
                + {translationService.t('system.choose.files')} {translationService.t('intake.choose.files.accept')}
              </span>
            </label>
          </React.Fragment>
        )}
        {error && <ErrorText transKey={error} />}
        {errors && errors.map((error, key) => <ErrorText key={key}>{error}</ErrorText>)}
      </div>
    )
  }
}

const mapDispatchToProps = dispatch => ({
  startDocumentUploading: () => dispatch(startDocumentUploading()),
  endDocumentUploading: () => dispatch(endDocumentUploading()),
})

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(UploadField)



// WEBPACK FOOTER //
// ./src/components/Form/UploadField/index.js